import React, { useState } from "react";
import { AnalyticsBox } from "./AnalyticsBox";
import { AnalyticsButton } from "./AnalyticsButton";
import { LineChart, XAxis, YAxis, CartesianGrid, Line, BarChart, Bar, Legend, Tooltip, ResponsiveContainer } from "recharts";

export const AnalyticsFinancials = ({ optimizationResults }) => {
  const [selectedChart, setSelectedChart] = useState("Daily Operation Cost");

  const financeData = optimizationResults?.data?.finance;
  const tcoAnalysis = financeData?.tco_analysis;

  const dieselDailyCosts = financeData?.cost_overview?.daily_costs?.diesel || {};
  const electricDailyCosts = financeData?.cost_overview?.daily_costs?.electric || {};
  const dieselAnnualCosts = financeData?.cost_overview?.annual_costs?.diesel || {};
  const electricAnnualCosts = financeData?.cost_overview?.annual_costs?.electric || {};
  const upfrontCosts = financeData?.cost_overview?.upfront_costs || {};

  const cumulativeCostData = Array.from(
    { length: tcoAnalysis?.parameters?.years + 1 || 11 },
    (_, i) => ({
      year: i,
      Diesel: tcoAnalysis?.cumulative_costs?.pure_diesel_fleet[i] || 0,
      Electric: tcoAnalysis?.cumulative_costs?.optimized_fleet[i] || 0,
    })
  );

  const chartData = {
    "Daily Operation Cost": [
      { category: "Energy Cost", Diesel: dieselDailyCosts.energy_cost, Electric: (electricDailyCosts.depot_energy || 0) + (electricDailyCosts.depot_demand || 0) + (electricDailyCosts.public_charging_energy || 0) },
      { category: "Vehicle Cost", Diesel: dieselDailyCosts.vehicle_cost, Electric: electricDailyCosts.vehicle_cost },
      { category: "Insurance Cost", Diesel: dieselDailyCosts.insurance_cost, Electric: electricDailyCosts.insurance_cost },
      { category: "Maintenance Cost", Diesel: dieselDailyCosts.maintenance_cost, Electric: electricDailyCosts.maintenance_cost },
      { category: "Non-ZEV Penalties", Diesel: dieselDailyCosts.non_ze_penalties, Electric: 0 },
      { category: "Energy Sold Revenue", Diesel: 0, Electric: electricDailyCosts.energy_sold_revenue, isRevenue: true },
    ],
    "Annual Operation Cost": [
        { category: "Energy Cost", Diesel: dieselAnnualCosts.energy_cost, Electric: (electricAnnualCosts.depot_energy || 0) + (electricAnnualCosts.depot_demand || 0) + (electricAnnualCosts.public_charging_energy || 0) },
        { category: "Vehicle Cost", Diesel: dieselAnnualCosts.vehicle_cost, Electric: electricAnnualCosts.vehicle_cost },
        { category: "Insurance Cost", Diesel: dieselAnnualCosts.insurance_cost, Electric: electricAnnualCosts.insurance_cost },
        { category: "Maintenance Cost", Diesel: dieselAnnualCosts.maintenance_cost, Electric: electricAnnualCosts.maintenance_cost },
        { category: "Non-ZEV Penalties", Diesel: dieselAnnualCosts.non_ze_penalties, Electric: 0 },
        { category: "Energy Sold Revenue", Diesel: 0, Electric: electricAnnualCosts.energy_sold_revenue, isRevenue: true },
    ],
    "Upfront Cost": [
      { category: "Vehicles", Diesel: upfrontCosts.diesel?.vehicle_cost, Electric: upfrontCosts.electric?.vehicle_cost, description: "Total vehicle purchase costs" },
      { category: "Infrastructure", Diesel: 0, Electric: (upfrontCosts.electric?.depot_construction || 0) + (upfrontCosts.electric?.charger_installation || 0) + (upfrontCosts.electric?.power_interconnection || 0) + (upfrontCosts.electric?.solar_installation || 0) + (upfrontCosts.electric?.battery_installation || 0), description: "Total infrastructure costs", isTotal: true },
    ],
    "Cumulative Cost": cumulativeCostData,
  };

  Object.values(chartData).flat().forEach(item => {
    item.Diesel = parseFloat((item.Diesel || 0).toFixed(2));
    item.Electric = parseFloat((item.Electric || 0).toFixed(2));
  });

  const isLineChart = chartType => chartType === "Cumulative Cost";

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const isCumulative = selectedChart === "Cumulative Cost";
      const formattedLabel = isCumulative && (label === "0" || label === 0) ? "Initial Year" : (isCumulative ? `Year ${label}` : label);
      
      return (
        <div className="p-3 bg-white border border-gray-300 rounded-lg shadow-lg text-sm">
          <p className="font-bold mb-2">{formattedLabel}</p>
          {payload.map((pld, index) => (
            <p key={index} style={{ color: pld.color }}>
              {`${pld.name}: $${pld.value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
            </p>
          ))}
          {isCumulative && label === 0 && <p className="text-xs italic mt-1">Includes upfront costs less residuals.</p>}
        </div>
      );
    }
    return null;
  };
  
  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-6 justify-center">
        <AnalyticsBox title={"Daily Operation Cost"} value={financeData?.cost_overview?.daily_costs?.operation || 0} />
        <AnalyticsBox title={"Annual Operation Cost"} value={financeData?.cost_overview?.annual_costs?.operation || 0} />
        <AnalyticsBox title={"Upfront Cost"} value={financeData?.cost_overview?.upfront_costs?.total || 0} />
        <AnalyticsBox title={"Total Cost of Ownership (10 years)"} value={tcoAnalysis?.financial_metrics?.total_cost_of_ownership?.optimized_fleet || 0} />
      </div>

      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex flex-col md:flex-row gap-8">
          <div className="flex md:flex-col flex-wrap gap-4 justify-between">
            {Object.keys(chartData).map((key) => (
              <AnalyticsButton
                key={key}
                label={key}
                handler={() => setSelectedChart(key)}
                isSelected={selectedChart === key}
              />
            ))}
          </div>

          <div className="flex-1 min-h-[350px]">
            <ResponsiveContainer width="100%" height={350}>
              {isLineChart(selectedChart) ? (
                <LineChart data={chartData[selectedChart]} margin={{ top: 5, right: 20, left: 20, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="year" name="Year" unit="" label={{ value: "Year", position: "insideBottom", offset: -10 }} />
                  <YAxis tickFormatter={val => `$${val.toLocaleString()}`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line type="monotone" dataKey="Diesel" stroke="#E74C3C" strokeWidth={2} />
                  <Line type="monotone" dataKey="Electric" stroke="#2ECC71" strokeWidth={2} />
                </LineChart>
              ) : (
                <BarChart data={chartData[selectedChart]} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="category" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={val => `$${val.toLocaleString()}`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="Diesel" fill="#E74C3C" />
                  <Bar dataKey="Electric" fill="#2ECC71" />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}; 